<script setup lang="ts">
/**
 * Style 08 / 09 Rate intro (3190:102979, 3190:103037; mobile 3189:102931, 3189:102990).
 * After Styles: rate now or save and exit. No style checked — the main action is disabled.
 */
import { FAIR_STYLE_COVER_SIZES } from '@/widgets/profile/fair-v2/fair-profile-v2-my-style.data'
import { useI18n } from 'vue-i18n'
import { FairButton } from '@/ui/primitives/FairButton'
import { FairCheckboxIndicator } from '@/ui/primitives/FairCheckbox'
import { FairSkeleton } from '@/ui/primitives/FairSkeleton'

export interface StyleQuizRateRow {
  id: number
  name: string
  image: string | null
  /** BE-137 */
  imageSrcset?: string | null
  outfits: number
}

const props = defineProps<{
  rows: StyleQuizRateRow[]
  checked: number[]
  loading?: boolean
}>()

const emit = defineEmits<{
  'update:checked': [ids: number[]]
  back: []
  rate: []
  exit: []
}>()

const { t } = useI18n()

function toggle(id: number) {
  emit(
    'update:checked',
    props.checked.includes(id) ? props.checked.filter((item) => item !== id) : [...props.checked, id],
  )
}
</script>

<template>
  <div class="fair-quiz-stage fair-quiz-stage--fill">
    <section class="fair-quiz-card">
      <div class="fair-quiz-card__head">
        <h2 class="fair-quiz-card__title">
          {{ t('style_quiz.rate_intro.title') }}
        </h2>
        <p class="fair-quiz-card__subtitle">
          {{ t('style_quiz.rate_intro.subtitle') }}
        </p>
      </div>

      <div class="fair-quiz-card__content style-quiz-rate-intro__content">
        <template v-if="loading">
          <div
            v-for="index in 3"
            :key="index"
            class="style-quiz-rate-intro__row"
          >
            <FairSkeleton
              class="style-quiz-rate-intro__thumb"
              variant="media"
              :mark="false"
            />
            <FairSkeleton
              :width="120"
              :height="14"
            />
          </div>
        </template>
        <button
          v-for="row in rows"
          v-else
          :key="row.id"
          type="button"
          role="checkbox"
          class="style-quiz-rate-intro__row"
          :aria-checked="checked.includes(row.id)"
          :disabled="!row.outfits"
          @click="toggle(row.id)"
        >
          <FairCheckboxIndicator :checked="checked.includes(row.id)" />
          <span class="style-quiz-rate-intro__thumb">
            <img
              v-if="row.image"
              :src="row.image"
              :srcset="row.imageSrcset || undefined"
              :sizes="row.imageSrcset ? FAIR_STYLE_COVER_SIZES.thumb : undefined"
              alt=""
              loading="lazy"
            >
          </span>
          <span class="style-quiz-rate-intro__text">
            <span class="style-quiz-rate-intro__name">{{ row.name }}</span>
            <span class="style-quiz-rate-intro__meta">
              {{ t('style_quiz.rate_intro.outfits', { n: row.outfits }, row.outfits) }}
            </span>
          </span>
        </button>
      </div>

      <div class="fair-quiz-card__footer style-quiz-rate-intro__footer">
        <FairButton
          class="fair-quiz-card__back"
          variant="tonal"
          size="auto"
          :label="t('quiz.back')"
          @click="emit('back')"
        />
        <div class="style-quiz-rate-intro__actions">
          <FairButton
            class="style-quiz-rate-intro__exit"
            variant="flat"
            size="auto"
            :label="t('style_quiz.rate_intro.save_exit')"
            @click="emit('exit')"
          />
          <FairButton
            class="fair-quiz-card__continue"
            variant="primary"
            size="auto"
            :label="t('style_quiz.rate_intro.rate')"
            :disabled="!checked.length || loading"
            @click="emit('rate')"
          />
        </div>
      </div>
    </section>
  </div>
</template>
